async function fetchAPI(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const isStringifiedJSON = typeof options.body === 'string';
  
  const headers = isFormData
    ? {} // Let the browser set the headers for FormData
    : {
        "Content-Type": "application/json",
        ...options.headers,
      };

  // Auto-stringify objects that aren't FormData or already stringified
  const body = isFormData || isStringifiedJSON || !options.body 
    ? options.body 
    : JSON.stringify(options.body);

  if (!options.method) options.method = "GET";

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}${path}`,
    {
      ...options,
      headers,
      body,
      credentials: "include",
    }
  );

  return response;
}

export const adminApiService = {
  // Authentication
  adminLogin(credentials) {
    return fetchAPI("/admin/admin-login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  },

  verifyAdminAuth() {
    return fetchAPI("/public/auth/admin-authorize");
  },

  // Dashboard Analytics
  getTotalProducts() {
    return fetchAPI("/admin/products/total");
  },

  getTotalSales(period = "weekly") {
    return fetchAPI(`/admin/orders/total-amount?period=${period}`);
  },

  getTotalUsers() {
    return fetchAPI("/admin/users/total-users");
  },

  getAnalytics(page = 1, pageSize = 10) {
    return fetchAPI(`/admin/analytics?page=${page}&pageSize=${pageSize}`);
  },

  // Products Management
  getAllProducts() {
    return fetchAPI("/admin/products/all");
  },

  getProductsByCategory(categoryId) {
    return fetchAPI(`/admin/products/by-category/${categoryId}`);
  },

  getProductById(productId) {
    return fetchAPI(`/public/products/product-details/${productId}`);
  },

  addProduct(productData) {
    return fetchAPI("/admin/products", {
      method: "POST",
      body: productData, // FormData
    });
  },

  updateProduct(productId, productData) {
    return fetchAPI(`/admin/products/${productId}`, {
      method: "PUT",
      body: productData, // FormData
    });
  },

  deleteProduct(productId) {
    return fetchAPI(`/admin/products/${productId}`, {
      method: "DELETE",
    });
  },

  updateProductStatus(productId, statusData) {
    return fetchAPI(`/admin/products/${productId}/featured`, {
      method: "PUT",
      body: JSON.stringify(statusData),
    });
  },

  getProductSpecsFields() {
    return fetchAPI("/admin/product-specs-fields");
  },

  // Categories Management
  getAllCategories() {
    return fetchAPI("/admin/categories/all-category");
  },
  getSingleCategory(categoryId) {
    return fetchAPI(`/admin/categories/category/${categoryId}`);
  },
  updateMenuCategoryStatus(id, data = null) {
    return fetchAPI(`/admin/categories/category/${id}/menu-status`, {
      method: "PUT",
      body: data, // Can be FormData for video upload or null for status toggle
    });
  },

  addCategory(categoryData) {
    return fetchAPI("/admin/categories/add-category", {
      method: "POST",
      body: categoryData,
    });
  },
  deleteCategory(id) {
    return fetchAPI(`/admin/categories/category/${id}`, {
      method: "DELETE",
    });
  },

  updateCategory(categoryId, categoryData) {
    return fetchAPI(`/admin/categories/category/${categoryId}`, {
      method: "PUT",
      body: categoryData,
    });
  },



  // Orders Management
  getAllOrders() {
    return fetchAPI("/admin/orders/all-orders");
  },

  getOrderById(orderId) {
    return fetchAPI(`/admin/orders/single-order/${orderId}`);
  },

  updateOrderStatus(orderId, statusData) {
    return fetchAPI(`/admin/orders/update-status/${orderId}`, {
      method: "PUT",
      body: JSON.stringify(statusData),
    });
  },

  handoverToCourier(orderId, payload) {
    return fetchAPI(`/admin/courier/handover-to-courier/${orderId}`, {
      method: "POST",
      body: payload,
    });
  },

  // Partners Management
  getAllPartners() {
    return fetchAPI("/admin/partners/all-partners");
  },

  updatePartnerApproval(partnerId, approvalData) {
    return fetchAPI(`/admin/partners/partner/${partnerId}/approval`, {
      method: "PUT",
      body: JSON.stringify(approvalData),
    });
  },

  getPartnerById(partnerId) {
    return fetchAPI(`/admin/partners/partner/${partnerId}`);
  },

  // CMS - Content Management
  // Product Recommendations
  getProductRecommendationsBg() {
    return fetchAPI("/public/cms/get-product-recommendation-bg");
  },

  getProductRecommendations() {
    return fetchAPI("/public/cms/get-recommendation");
  },

  addProductRecommendation(recommendationData) {
    return fetchAPI("/admin/cms/product-recommendations", {
      method: "POST",
      body: recommendationData, // FormData
    });
  },

  updateProductRecommendation(recommendationData) {
    return fetchAPI("/admin/cms/product-recommendations", {
      method: "PUT",
      body: recommendationData, // FormData
    });
  },

  deleteProductRecommendation(recommendationId) {
    return fetchAPI(`/admin/cms/product-recommendations/${recommendationId}`, {
      method: "DELETE",
    });
  },

  updateProductRecommendationBg(bgData) {
    return fetchAPI("/admin/cms/product-recommendation-bg", {
      method: "PUT",
      body: bgData, // FormData
    });
  },

  // Terms & Conditions
  updateTermsConditions(content) {
    return fetchAPI("/admin/cms/terms-conditions", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  getTermsConditions() {
    return fetchAPI("/public/cms/get-terms-conditions");
  },

  // Privacy Policy
  updatePrivacyPolicy(content) {
    return fetchAPI("/admin/cms/privacy-policy", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  getPrivacyPolicy() {
    return fetchAPI("/public/cms/get-privacy-policy");
  },

  // Warranty Policy
  updateWarrantyPolicy(content) {
    return fetchAPI("/admin/cms/warranty-policy", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  getWarrantyPolicy() {
    return fetchAPI("/public/cms/get-warranty-policy");
  },

  // Delivery Policy
  getDeliveryPolicy() {
    return fetchAPI("/public/cms/get-delivery-policy");
  },

  updateDeliveryPolicy(content) {
    return fetchAPI("/admin/cms/delivery-policy", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  // Return Exchange Policy
  updateReturnExchangePolicy(content) {
    return fetchAPI("/admin/cms/return-exchange-policy", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  getReturnExchangePolicy() {
    return fetchAPI("/public/cms/get-return-exchange-policy");
  },

  // Social Media
  getSocialMedia() {
    return fetchAPI("/public/cms/get-social-media");
  },

  addSocialMedia(socialMediaData) {
    return fetchAPI("/admin/cms/social-media", {
      method: "POST",
      body: JSON.stringify(socialMediaData),
    });
  },

  updateSocialMedia(id, socialMediaData) {
    return fetchAPI(`/admin/cms/social-media/${id}`, {
      method: "PUT",
      body: JSON.stringify(socialMediaData),
    });
  },

  deleteSocialMedia(id) {
    return fetchAPI(`/admin/cms/social-media/${id}`, {
      method: "DELETE",
    });
  },

  // Category Sliders
  getCategorySliders() {
    return fetchAPI("/public/cms/get-category-sliders");
  },

  addCategorySlider(sliderData) {
    return fetchAPI("/admin/cms/category-sliders", {
      method: "POST",
      body: sliderData, // FormData
    });
  },

  updateCategorySlider(sliderData) {
    return fetchAPI("/admin/cms/category-sliders", {
      method: "PUT",
      body: sliderData, // FormData
    });
  },

  deleteCategorySlider(sliderId) {
    return fetchAPI(`/admin/cms/category-sliders/${sliderId}`, {
      method: "DELETE",
    });
  },

  // Product Sections
  getProductSections() {
    return fetchAPI("/public/cms/get-product-sections");
  },

  updateProductSection(sectionId, sectionData) {
    return fetchAPI(`/admin/cms/product-sections/${sectionId}`, {
      method: "PUT",
      body: sectionData, // FormData
    });
  },

  // FAQs
  getFaqs() {
    return fetchAPI("/public/cms/get-faqs");
  },

  addFaq(faqData) {
    return fetchAPI("/admin/cms/faqs", {
      method: "POST",
      body: JSON.stringify(faqData),
    });
  },

  updateFaq(faqData) {
    return fetchAPI("/admin/cms/faqs", {
      method: "PUT",
      body: JSON.stringify(faqData),
    });
  },

  deleteFaq(faqId) {
    return fetchAPI(`/admin/cms/faqs/${faqId}`, {
      method: "DELETE",
    });
  },

  toggleFaqVisibility(visibilityData) {
    return fetchAPI("/admin/cms/faqs/toggle-visibility", {
      method: "PUT",
      body: JSON.stringify(visibilityData),
    });
  },

  // Our Features
  getOurFeatures() {
    return fetchAPI("/public/cms/get-features");
  },

  updateOurFeature(featureData) {
    return fetchAPI("/admin/cms/features", {
      method: "PUT",
      body: JSON.stringify(featureData),
    });
  },

  deleteOurFeature(featureId) {
    return fetchAPI(`/cms/delete-our-feature/${featureId}`, {
      method: "DELETE",
    });
  },

  // Moving Text
  getMovingTexts() {
    return fetchAPI("/public/cms/get-moving-texts");
  },

  addMovingText(textData) {
    return fetchAPI("/admin/cms/moving-texts", {
      method: "POST",
      body: JSON.stringify(textData),
    });
  },

  updateMovingText(textData) {
    return fetchAPI("/admin/cms/moving-texts", {
      method: "PUT",
      body: JSON.stringify(textData),
    });
  },

  deleteMovingText(textId) {
    return fetchAPI(`/admin/cms/moving-texts/${textId}`, {
      method: "DELETE",
    });
  },

  // Online Shops
  getOnlineShops() {
    return fetchAPI("/public/cms/get-online-shops");
  },

  addOnlineShop(shopData) {
    return fetchAPI("/admin/cms/online-shops", {
      method: "POST",
      body: shopData, // FormData
    });
  },

  updateOnlineShop(shopId, shopData) {
    return fetchAPI(`/admin/cms/online-shops/${shopId}`, {
      method: "PUT",
      body: shopData, // FormData
    });
  },

  deleteOnlineShop(shopId) {
    return fetchAPI(`/admin/cms/online-shops/${shopId}`, {
      method: "DELETE",
    });
  },

  // Custom Product Grid
  getCustomProductGrid() {
    return fetchAPI("/public/cms/get-custom-product-grid");
  },

  updateCustomProductGrid(gridId, gridData) {
    return fetchAPI(`/admin/cms/custom-product-grid/${gridId}`, {
      method: "PUT",
      body: JSON.stringify(gridData),
    });
  },

  getCustomProductGridAdmin() {
    return fetchAPI("/public/cms/get-custom-product-grid");
  },

  // Popup Settings


  getPopupSettingsPublic() {
    return fetchAPI("/public/cms/popup-settings");
  },

  addPopupSettings(popupData) {
    return fetchAPI("/admin/cms/popup-settings", {
      method: "POST",
      body: popupData, // FormData
    });
  },

  updatePopupSettings(popupData) {
    return fetchAPI("/admin/cms/popup-settings", {
      method: "PUT",
      body: popupData, // FormData
    });
  },

  deletePopupImage(imageId) {
    return fetchAPI(`/admin/cms/popup-settings/${imageId}`, {
      method: "DELETE",
    });
  },

  togglePopupImage(imageId, status) {
    return fetchAPI(`/admin/cms/popup-settings/${imageId}/toggle`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  },

  // Manual Orders
  initManualOrder(orderData) {
    return fetchAPI("/user/payment/init/manual-order", {
      method: "POST",
      body: JSON.stringify(orderData),
    });
  },

  // Courier Services
  getCityList() {
    return fetchAPI("/public/courier/city-list", {
      method: "POST",
    });
  },

  getAreaByDistrict(district) {
    return fetchAPI("/public/courier/area-by-district", {
      method: "POST",
      body: JSON.stringify({ district }),
    });
  },

  // Utility function to build image URLs
  buildImageUrl(path, filename) {
    return `${process.env.REACT_APP_API_URL_Plain}/config/uploads/${path}/${filename}`;
  },

  // Common image paths
  getProductImageUrl(filename) {
    return this.buildImageUrl("products/product_images", filename);
  },

  getBannerImageUrl(filename) {
    return this.buildImageUrl("products/banner_images", filename);
  },

  getCategorySliderImageUrl(filename) {
    return this.buildImageUrl("category_slider", filename);
  },

  getProductRecommendationImageUrl(filename) {
    return this.buildImageUrl("product_recommendations", filename);
  },

  getProductSectionImageUrl(filename) {
    return this.buildImageUrl("product_section_image", filename);
  },

  getOnlineShopImageUrl(filename) {
    return this.buildImageUrl("online_shop", filename);
  },

  getPopupImageUrl(filename) {
    return this.buildImageUrl("popup_images", filename);
  },

  getProductReview() {
    return fetchAPI("/public/products/product-review");
  },

  approveProductReview(payload) {
    return fetchAPI(`/admin/product-reviews/approve`, {
      method: "POST",
      body: payload,
    });
  },

  deleteProductReview(reviewId) {
    return fetchAPI(`/admin/product-reviews/${reviewId}`, {
      method: "DELETE",
    });
  },

  getHeroMedia() {
    return fetchAPI("/public/cms/get-hero-media");
  },

  addHeroMedia(payload) {
    return fetchAPI(`/admin/cms/hero-media`, {
      method: "POST",
      body: payload,
    });
  },

  updateHeroMedia(payload) {
    return fetchAPI(`/admin/cms/hero-media`, {
      method: "PUT",
      body: payload,
    });
  },

  getBlogs() {
    return fetchAPI("/admin/blogs/all-blogs");
  },

  getFooterSettings() {
    return fetchAPI("/public/cms/get-footer-settings");
  },

  updateFooterSettings(payload) {
    return fetchAPI("/admin/cms/footer-settings", {
      method: "PUT",
      body: payload,
    });
  },

  addFooterSettings(payload) {
    return fetchAPI("/admin/cms/footer-settings", {
      method: "POST",
      body: payload,
    });
  },

  getFooterSeo() {
    return fetchAPI("/public/cms/get-seo-content");
  },

  updateFooterSeo(content) {
    return fetchAPI("/admin/cms/seo-content", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

  // Coupons Management
  getAllCoupons() {
    return fetchAPI("/admin/coupons");
  },
  getCouponById(id) {
    return fetchAPI(`/admin/coupons/${id}`);
  },
  addCoupon(couponData) {
    return fetchAPI("/admin/coupons", {
      method: "POST",
      body: JSON.stringify(couponData),
    });
  },
  updateCoupon(id, couponData) {
    return fetchAPI(`/admin/coupons/${id}`, {
      method: "PUT",
      body: JSON.stringify(couponData),
    });
  },
  deleteCoupon(id) {
    return fetchAPI(`/admin/coupons/${id}`, {
      method: "DELETE",
    });
  },

  validateCoupon(payload){
    return fetchAPI(`/user/cart/validate-coupon`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // About Us
  getAboutUs() {
    return fetchAPI("/public/cms/get-about-us");
  },
  updateAboutUs(content) {
    return fetchAPI("/admin/cms/about-us", {
      method: "PUT",
      body: JSON.stringify({ content }),
    });
  },

}; 